import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Settings, Building2, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FEATURE_GROUPS = [
  { label: 'Core (Always On)', features: [
    { key: 'stack_map', label: 'Tech Stack Map', desc: 'Interactive visualization', locked: true },
    { key: 'tech_database', label: 'Tech Database', desc: 'Platforms, products, vendors', locked: true },
    { key: 'basic_reports', label: 'Basic Reports', desc: 'Coverage, adoption, vendors', locked: true },
    { key: 'property_dashboard', label: 'Property Dashboard', desc: 'KPIs and stats', locked: true },
  ]},
  { label: 'Add-On Modules', features: [
    { key: 'corporate_stack', label: 'Corporate Stack', desc: 'Push solutions to properties' },
    { key: 'governance', label: 'Governance Suite', desc: 'Responsibility tracking, SSO audit' },
    { key: 'compliance', label: 'Brand Compliance & ESG', desc: 'Brand standards, ESG diversity' },
    { key: 'contracts', label: 'Contract Management', desc: 'Contracts, renewals, cost tracking' },
    { key: 'rfp_management', label: 'RFP & Marketplace', desc: 'RFP generator, product suggestions' },
    { key: 'benchmarking', label: 'Industry Benchmarks', desc: 'Anonymized comparisons (coming soon)' },
  ]},
  { label: 'Report Access', features: [
    { key: 'advanced_reports', label: 'Advanced Reports', desc: 'All 30+ reports' },
    { key: 'executive_reports', label: 'Executive Reports', desc: 'Executive summary, maturity' },
    { key: 'risk_reports', label: 'Risk Reports', desc: 'Tech debt, vendor risk, drift' },
  ]},
  { label: 'Export & Delivery', features: [
    { key: 'export_pdf', label: 'PDF Export', desc: 'Download reports as PDF', locked: true },
    { key: 'export_csv', label: 'CSV Export', desc: 'Download data as CSV' },
    { key: 'export_email', label: 'Email Reports', desc: 'Send reports via email' },
  ]},
  { label: 'Portals', features: [
    { key: 'vendor_portal', label: 'Vendor Portal', desc: 'Vendor self-service' },
    { key: 'brand_portal', label: 'Brand Portal', desc: 'Brand standard management' },
  ]},
];

export default function TenantFeatureManager({ token }) {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [features, setFeatures] = useState({});
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/tenants`, { headers })
      .then(res => setTenants(res.data.tenants || [])).catch(() => {});
  }, [headers]);

  const selectTenant = async (tenant) => {
    setSelectedTenant(tenant);
    try {
      const switchRes = await axios.post(`${BACKEND_URL}/api/admin/tenants/${tenant.id}/switch`, {}, { headers });
      const featRes = await axios.get(`${BACKEND_URL}/api/tenant-features`, { headers: { Authorization: `Bearer ${switchRes.data.token}` } });
      setFeatures(featRes.data);
    } catch { toast.error('Failed to load features'); }
  };

  const toggleFeature = (key) => {
    setFeatures(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const save = async () => {
    if (!selectedTenant) return;
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/tenants/${selectedTenant.id}/features`, features, { headers });
      toast.success(`Features updated for ${selectedTenant.name}`);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="tenant-features">
      <div>
        <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><Settings className="h-5 w-5" /> Tenant Feature Controls</h2>
        <p className="text-sm text-muted-foreground">Enable or disable features per management company for tiered monetization.</p>
      </div>

      {/* Tenant selector */}
      <div className="flex gap-2 flex-wrap">
        {tenants.map(t => (
          <button key={t.id} onClick={() => selectTenant(t)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${selectedTenant?.id === t.id ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted/40'}`}>
            <Building2 className="h-3.5 w-3.5" /> {t.name}
            <Badge variant="secondary" className="text-[9px] font-mono">{t.property_count} props</Badge>
          </button>
        ))}
      </div>

      {selectedTenant && (
        <>
          <Separator />
          <ScrollArea className="max-h-[500px]">
            <div className="space-y-6">
              {FEATURE_GROUPS.map(group => (
                <div key={group.label}>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{group.label}</h3>
                  <div className="space-y-2">
                    {group.features.map(f => (
                      <div key={f.key} className={`flex items-center justify-between p-3 rounded-lg border ${f.locked ? 'bg-muted/30' : 'bg-card'}`}>
                        <div>
                          <span className={`text-sm font-medium ${f.locked ? 'text-muted-foreground' : ''}`}>{f.label}</span>
                          <p className="text-xs text-muted-foreground">{f.desc}</p>
                          {f.locked && <span className="text-[9px] text-muted-foreground/60 italic">Always included in plan</span>}
                        </div>
                        <Switch
                          checked={features[f.key] ?? false}
                          onCheckedChange={() => !f.locked && toggleFeature(f.key)}
                          disabled={f.locked}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          <Button onClick={save} disabled={saving} className="gap-1.5">
            <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save Feature Settings'}
          </Button>
        </>
      )}
    </div>
  );
}
